import {Plural, Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {legacyInboxColorToPaletteColor} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {ChannelsCell} from '../../components/common/table/cells/channelsCell';
import {EmptyCell} from '../../components/common/table/cells/emptyCell';
import {IconCell} from '../../components/common/table/cells/iconCell';
import {NumberCell} from '../../components/common/table/cells/numberCell';
import {CellButton} from '../../components/common/table/helpers/cellButton';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowCheckboxWrapperWidth,
  virtualTableRowOffset,
} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {TableColumn} from '../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../components/common/table/tableRow';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
} from '../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {InboxWithChannelsModel, useFetchInboxesAndChannels} from '../fetchers/inboxes';

/*
 * Props.
 */

interface SettingsInboxesListTableProps {
  searchQuery: string;
  url: SettingsInboxesUrl;
}

/*
 * Style.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  padding-left: ${virtualTableRowCheckboxWrapperWidth}px;
`;

const StyledTableRow = styled(TableRow)`
  min-height: 56px;
`;

const StyledCellButton = styled(CellButton)`
  margin-left: 4px;
  padding-left: 8px;
  padding-right: 8px;
  width: auto;
`;

/*
 * Component.
 */

enum InboxesTableColumnsEnum {
  NAME = 'NAME',
  CHANNELS = 'CHANNELS',
  MOVE_RULES = 'MOVE_RULES',
}

const columns: ReadonlyArray<TableColumn<InboxesTableColumnsEnum>> = [
  {
    id: InboxesTableColumnsEnum.NAME,
    type: InboxesTableColumnsEnum.NAME,
    title: <Trans id="settingstable-name">Name</Trans>,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      sticky: {
        isSticky: true,
      },
      cellWidth: {
        shouldFillRemainingWidth: true,
        minWidth: 330 - virtualTableRowOffset,
        maxWidth: 330 - virtualTableRowOffset,
      },
    },
  },
  {
    id: InboxesTableColumnsEnum.CHANNELS,
    type: InboxesTableColumnsEnum.CHANNELS,
    title: <Trans id="settingstable-channels">Channels</Trans>,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      cellWidth: {
        width: 430,
      },
    },
  },
  {
    id: InboxesTableColumnsEnum.MOVE_RULES,
    type: InboxesTableColumnsEnum.MOVE_RULES,
    title: <Trans id="settingstable-moverules">Move rules</Trans>,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      cellWidth: {
        width: 170,
      },
    },
  },
];

export const SettingsInboxesListTable: FC<SettingsInboxesListTableProps> = (props) => {
  const {items, moreStatus, loadMore} = useFetchInboxesAndChannels(props.searchQuery, props.url.namespace);
  const {navigateToLink} = useLinkNavigation();
  const theme = useUserTheme();

  const renderCell = (
    item: InboxWithChannelsModel,
    index: number,
    column: TableColumn<InboxesTableColumnsEnum>,
    isHovering: boolean,
    options: TableRowRenderOptions,
  ) => {
    const inboxPaletteColor = legacyInboxColorToPaletteColor(item.inbox.color, theme);
    const emptyCell = (
      <EmptyCell
        key={column.type}
        settings={column.settings}
        isHovering={isHovering}
        index={index}
        size={options.size}
      />
    );

    switch (column.type) {
      case InboxesTableColumnsEnum.NAME: {
        return (
          <IconCell
            key={InboxesTableColumnsEnum.NAME}
            index={index}
            isHovering={isHovering}
            shouldShowStickyGradient={options.shouldShowStickyGradient}
            value={item.inbox.name}
            size={options.size}
            iconName="inboxFilled"
            iconColor={theme.greys.white}
            backgroundColor={theme.palette[inboxPaletteColor].shade40}
            settings={column.settings}
          />
        );
      }

      case InboxesTableColumnsEnum.CHANNELS: {
        if (item.channels.length < 1) {
          return emptyCell;
        }

        return (
          <ChannelsCell
            key={InboxesTableColumnsEnum.CHANNELS}
            index={index}
            isHovering={isHovering}
            shouldShowStickyGradient={options.shouldShowStickyGradient}
            shouldRemoveLeftBorderRadius
            value={item.channels}
            size={options.size}
            settings={column.settings}
            onChannelClick={(channel) => {
              navigateToLink(
                buildSettingsPath({
                  ...props.url,
                  inboxId: item.inbox.id,
                  channelId: channel.id,
                  resourcePage: SettingsResourcePagesEnum.EDIT,
                  subPage: SettingsInboxesSubPagesEnum.CHANNELS,
                }),
              );
            }}
            onOverflowClick={() => {
              navigateToLink(
                buildSettingsPath({
                  ...props.url,
                  inboxId: item.inbox.id,
                  resourcePage: SettingsResourcePagesEnum.EDIT,
                  subPage: SettingsInboxesSubPagesEnum.CHANNELS,
                }),
              );
            }}
          />
        );
      }

      case InboxesTableColumnsEnum.MOVE_RULES: {
        if (item.inbox.numMoveBundles < 1) {
          return emptyCell;
        }

        return (
          <NumberCell
            key={InboxesTableColumnsEnum.MOVE_RULES}
            index={index}
            isHovering={isHovering}
            size={options.size}
            shouldShowStickyGradient={options.shouldShowStickyGradient}
            shouldRemoveLeftBorderRadius
            value={item.inbox.numMoveBundles}
            renderValue={(value) => (
              <StyledCellButton
                avatar={<Icon name="gearSmall" color={theme.greys.shade70} />}
                avatarSizePx={16}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToLink(
                    buildSettingsPath({
                      ...props.url,
                      inboxId: item.inbox.id,
                      resourcePage: SettingsResourcePagesEnum.EDIT,
                      subPage: SettingsInboxesSubPagesEnum.RULES,
                    }),
                  );
                }}
              >
                <Plural
                  id="settingsinboxes-moverules"
                  value={value}
                  zero="No move rules"
                  one="1 move rule"
                  other="# move rules"
                />
              </StyledCellButton>
            )}
            settings={column.settings}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const renderRow: VirtualTableRowRenderer<InboxWithChannelsModel> = (item, options) => (
    <StyledTableRow
      hasHover
      onSelect={(e) => {
        if (e.defaultPrevented) {
          return;
        }
        navigateToLink(
          buildSettingsPath({
            ...props.url,
            inboxId: item.inbox.id,
            resourcePage: SettingsResourcePagesEnum.EDIT,
            subPage: SettingsInboxesSubPagesEnum.OVERVIEW,
          }),
        );
      }}
      renderColumn={(isHovering) =>
        columns.map((column, index) => renderCell(item, index, column, isHovering, options))
      }
    />
  );

  return (
    <StyledTableContainerDiv>
      <StaticVirtualTable<InboxWithChannelsModel>
        items={items}
        columns={columns}
        hasData={moreStatus !== AsyncMoreStatusesEnum.LOADING && items.length > 0}
        moreStatuses={{
          [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
          [ScrollDirectionsEnum.BOTTOM]: moreStatus,
        }}
        shouldRenderEmptyState={moreStatus === AsyncMoreStatusesEnum.ALL_LOADED && items.length === 0}
        onLoadMore={loadMore}
        renderRow={renderRow}
      />
    </StyledTableContainerDiv>
  );
};
